/* eslint-disable no-console */
import './fonts.css';
import './style.css';
import axios from 'axios';
import {
    DafInMemoryCacheStrategy,
    cacheToken,
    DafExchangeStrategy,
    DafAnonymousAuthStrategy,
    DafGrantType,
    start,
    registerSpaComponent,
    AuthConfig
} from '@nab/x-spa-react';
import LoginSpa from './components/LoginSpa';
import PortalSpa from './components/PortalSpa';
import GlobalSpa from './components/GlobalSpa';
import getContextFromNavManager from './utils/getContextFromNavManager';
import getFlagsConfig from './utils/getFlagsConfig';
import { UserOrg } from '@nab/x-types';
import { Logout } from './components/helper';
import { ShellContainer } from './shellContainer';


/**
 * Retrieve SPA context config
 */

// Ideally return type ContextConfig but this is causing issues with the data type...
async function spaContextProvider(contextId: string): Promise<any> {
    try {
        if (contextId === 'root-config') {
            return (await axios.get(`config.json`)).data;
        } else {
            return (
                (contextId === 'root2' && (await getContextFromNavManager(contextId))) ||
                (await axios.get(`portal/config/context-${contextId}.json`)).data
            );
        }
    } catch (e) {
        throw new Error(`Failed to retrieve context ${contextId}`);
    }
}

registerSpaComponent('medfin-identity', LoginSpa );
registerSpaComponent('medfin-miniapp', PortalSpa );

window.addEventListener('load', async () => {
    try {
        const shellConfig = await spaContextProvider('root-config');
        const dafConfig = shellConfig['daf']['default'];

        const hostElement = document.getElementById('root');
        if (!hostElement) {
            throw new Error('Shell host element #root was not found in index.html');
        }

        const spaShell = await start({
            rootComponent: GlobalSpa,
            Container: ShellContainer,
            rootContextId: 'medfin-global',

            authConfig: {
                context: 'external',
                environment: dafConfig['environment'],
                tokenType: 'Bearer',
                // @ts-ignore
                tenant: 'medfin',
                tokenSource: 'DAF',
                scopes: 'idp:user',
                requestTokenOnLoad: false
            },

            authStrategies: [
                new DafAnonymousAuthStrategy(dafConfig['dafApiUrl'], dafConfig['clientId']),
                new DafExchangeStrategy(dafConfig['dafApiUrl'])
            ],

            authTokenCacheStrategy: new DafInMemoryCacheStrategy(),
            spaContextProvider,

            userConfig: {
                type: 'customer',
                org: 'Medfin' as UserOrg,
            },
            servicesConfig: getFlagsConfig(shellConfig),

            brand: { id: 'medfin' },
            hostElement
        });

        spaShell.config.actions.addEventListener('app_start', async () => {
            if (sessionStorage.bearerToken) {
                await cacheToken({
                    tokenValue: sessionStorage.bearerToken,
                    config: {
                        // @ts-ignore
                        isSeed: true,
                        tokenType: 'Bearer',
                        tokenSource: 'DAF',
                        tenant: 'medfin',
                        context: 'external',
                        environment: dafConfig['environment']
                    }
                });
            } else {
                try {
                    // @ts-ignore - authenticate() signature does not expose isSeed/grantType in its public types but these fields are required by DafAnonymousAuthStrategy.supports() to match and pre-cache the anonymous seed token
                    await spaShell.config.actions.authenticate({
                        isSeed: true,
                        tokenType: 'Bearer',
                        tokenSource: 'DAF',
                        context: 'external',
                        grantType: DafGrantType.nabAnonymous,
                        tenant: 'medfin',
                        scopes: 'idp:user',
                        environment: dafConfig['environment']
                    });
                } catch (e) {
                    console.error('Failed to pre-cache anonymous seed token:', e);
                }
                await spaShell.config.actions.navigate('/portal/login/identity');
            }
        });
        spaShell.config.actions.addEventListener('user_authenticated', async data => {
            const config: AuthConfig = {
                // @ts-ignore
                isSeed: true,
                tokenType: 'Bearer',
                tokenSource: 'DAF',
                tenant: 'medfin',
                context: 'external',
                environment: dafConfig['environment']
            };

            if (data) {
                await cacheToken({
                    tokenValue: data.bearerToken,
                    config
                });
                await spaShell.config.actions.navigate('/portal/main');
            } else if (sessionStorage.bearerToken) {
                await cacheToken({
                    tokenValue: sessionStorage.bearerToken,
                    config
                });
                await spaShell.config.actions.navigate('/portal/main');
            }
        });
        spaShell.config.actions.addEventListener('user_timeout', async () => {
            alert('Something went wrong OR Your session has expired. Please login again & try. If the issue still persists, please contact Medfin Client Services on 02 8074 3242 or 1300 728 718 during business hours, from Monday to Friday, 8:30am – 5:30pm AEST');
            window.history.replaceState({}, document.title, window.location.origin);
            sessionStorage.clear();
            window.location.reload();
        });
        // Forcefully logs out user, doing necessary acttions as defined in logout function like revoking token, etc.
        spaShell.config.actions.addEventListener('force_logout', async () => {
            Logout();
        });
        // Forcefully logs out user, doing necessary acttions as defined in logout function like revoking token, etc.
        spaShell.config.actions.addEventListener('force_logout_updated_terms', async () => {
            alert('Newest version of Terms and Conditions have not been accepted. Please log in again and accept to continue using this application.')
            Logout();
        });
        console.log('SPA started successfully');
    } catch (e) {
        console.error('SPA failed to start', e);
    }
});
